import React from 'react';
import { Box, Text, rem } from '@mantine/core';
import { spotlightSelectFirst } from '@/lib/spotlight/spotlight.store';
import { IconArrowRight, IconFolderOpen, IconHistory } from '@tabler/icons-react';
import type { TypeNavNode } from '@/types/ui/types.ui.navigation';
import SpotlightEmpty from './Spotlight.Empty';
import SpotlightRow from './Spotlight.Row';

type Props = {
    level: TypeNavNode | null;
    items: TypeNavNode[];
    recent: TypeNavNode[];
    onEnter: (node: TypeNavNode) => void;
    onOpen: (node: TypeNavNode) => void;
};

const GroupLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Text fz={10} fw={700} tt="uppercase" c="dimmed" px="sm" pt="sm" pb={4} style={{ letterSpacing: '0.09em' }}>
        {children}
    </Text>
);

const SpotlightLevel: React.FC<Props> = ({ level, items, recent, onEnter, onOpen }) => {
    React.useEffect(() => {
        const frame = requestAnimationFrame(() => spotlightSelectFirst());
        return () => cancelAnimationFrame(frame);
    }, [level?.id]);

    if (items.length === 0) {
        return (
            <SpotlightEmpty
                icon={IconFolderOpen}
                title={`${level?.label ?? 'Questo ramo'} non ha sottovoci`}
                body="Premi ← per risalire, oppure Invio per aprire la pagina."
            />
        );
    }

    return (
        <Box pb={4}>
            {level ? (
                <>
                    <GroupLabel>{level.label}</GroupLabel>
                    {level.expandOnly ? null : (
                        <SpotlightRow
                            icon={IconArrowRight}
                            label={`Apri ${level.label}`}
                            hint={level.description}
                            onSelect={() => onOpen(level)}
                            closeOnSelect
                            tone="brand"
                        />
                    )}
                    <GroupLabel>Sottovoci</GroupLabel>
                </>
            ) : null}

            {!level && recent.length > 0 ? (
                <>
                    <GroupLabel>Recenti</GroupLabel>
                    {recent.map((node) => (
                        <SpotlightRow
                            key={`recent-${node.id}`}
                            icon={IconHistory}
                            label={node.label}
                            hint={node.description}
                            onSelect={() => onOpen(node)}
                            closeOnSelect
                        />
                    ))}
                    <GroupLabel>Naviga</GroupLabel>
                </>
            ) : null}

            {items.map((node) => {
                const isBranch = Boolean(node.children?.length);
                return (
                    <SpotlightRow
                        key={node.id}
                        icon={node.icon}
                        label={node.label}
                        hint={node.description}
                        branchId={isBranch ? node.id : undefined}
                        onSelect={() => (isBranch ? onEnter(node) : onOpen(node))}
                        closeOnSelect={!isBranch}
                        tone={!level ? 'brand' : 'neutral'}
                    />
                );
            })}
            <Box h={rem(2)} />
        </Box>
    );
};

export default SpotlightLevel;
